import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { utcToEventLocal, eventLocalToUtc, tzShortLabel } from '@/utils/tzInput';

/**
 * Bug X4: single source of truth for datetime inputs that must be interpreted
 * in the EVENT's IANA timezone rather than the admin's browser local.
 *
 * Props:
 *   - value: stored ISO string (may or may not have offset). null/undefined ok.
 *   - onChange(iso): fires with a fully-qualified ISO string (offset baked in).
 *     If the input is cleared, fires with null.
 *   - tz: IANA timezone name (e.g. "America/Chicago"). REQUIRED to avoid
 *     silently falling back to browser local. If tz is invalid we render a
 *     disabled input + hint asking admin to fix the event timezone.
 *   - label: optional label text shown above the input.
 *   - hint: optional additional help text under the input.
 *   - includeSeconds: rarely needed. defaults false.
 *   - required, disabled, className, data-testid: forwarded through.
 *
 * On the wire:
 *   Input value             -> "YYYY-MM-DDTHH:MM"  wall-clock in tz
 *   Stored / emitted value  -> ISO with correct offset for tz at that moment
 */
const TzAwareDatetimeInput = ({
  value,
  onChange,
  tz,
  label,
  hint,
  disabled = false,
  className = '',
  ...rest
}) => {
  const isValidTz = (() => {
    if (!tz || typeof tz !== 'string') return false;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
      return true;
    } catch { return false; }
  })();

  const localValue = React.useMemo(
    () => (isValidTz ? utcToEventLocal(value, tz) : ''),
    [value, tz, isValidTz],
  );

  const handleChange = (e) => {
    const s = e.target.value;
    if (!s) return onChange?.(null);
    if (!isValidTz) return; // guarded by disabled state
    const iso = eventLocalToUtc(s, tz);
    onChange?.(iso);
  };

  // Compute the friendly zone abbreviation for the CURRENTLY-typed wall clock
  // (handles DST transitions - e.g. same input reads CST in January, CDT in July).
  const short = React.useMemo(() => {
    if (!isValidTz) return '';
    if (!localValue) return tzShortLabel(tz, new Date());
    try {
      const asUtc = new Date(`${localValue}:00Z`);
      return tzShortLabel(tz, asUtc) || tz;
    } catch { return tz; }
  }, [localValue, tz, isValidTz]);

  return (
    <div className={className}>
      {label && (
        <Label className="text-[11px] uppercase tracking-widest text-secondary-700 mb-1 block">{label}</Label>
      )}
      <Input
        type="datetime-local"
        value={localValue}
        onChange={handleChange}
        disabled={disabled || !isValidTz}
        data-testid={rest['data-testid'] || 'tz-datetime-input'}
        {...rest}
      />
      {isValidTz ? (
        <div className="text-[11px] text-neutral-500 mt-1">
          Interpreted as <strong className="text-neutral-700">{short || tz}</strong>
          {tz && short && short !== tz ? ` (${tz})` : ''}
          {hint ? ` - ${hint}` : ''}
        </div>
      ) : (
        <div className="text-[11px] text-accent-700 mt-1">
          Set a valid event timezone on the Basics tab before choosing dates.
        </div>
      )}
    </div>
  );
};

export default TzAwareDatetimeInput;
